import React, { useState, useEffect } from 'react';
import CommonInput from '../../Common/CommonInput';
import CommonSelect from '../../Common/CommonSelect';
import xeroService from '../../../services/xeroService';
import CommonTextarea from '../../Common/CommonTextarea';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';

const XeroForm = ({ initialData, onSave, isLoading, onClose }) => {
    const [formData, setFormData] = useState({
        xero_client_id: '',
        xero_client_secret: '',
        xero_redirect_uri: '',
        xero_status: {
            id: '1',
            label: 'Active'
        },
        access_token: '',
        refresh_token: '',
        xero_tenant: null,
        expires: '',
        refresh_token_expires: '',

        purchase_order_account: null,
        job_order_account: null,
        job_order_lpo_account: null,
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [tenants, setTenants] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [contactGroups, setContactGroups] = useState([]);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                xero_client_id: initialData.xero_client_id || '',
                xero_client_secret: initialData.xero_client_secret || '',
                xero_redirect_uri: initialData.xero_redirect_uri || '',
                xero_status: initialData.xero_status || {
                    id: '1',
                    label: 'Active'
                },
                access_token: initialData.access_token || '',
                refresh_token: initialData.refresh_token || '',
                xero_tenant: initialData.xero_tenant || initialData.tenant || null,
                expires: initialData.expires || '',
                refresh_token_expires: initialData.refresh_token_expires || '',

                purchase_order_account: initialData.purchase_order_account || null,
                job_order_account: initialData.job_order_account || null,
                job_order_lpo_account: initialData.job_order_lpo_account || null
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        setErrors(prev => ({...prev, [name]: ''}));
    };

    const handleSelectChange = async (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        setErrors(prev => ({...prev, [name]: ''}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const handleAuthorize = async () => {
        try {
            setIsFetching(true);
            const url = await xeroService.getAuthorizeUrl();
            setErrors({});
            window.location.href = url;
        } catch (error) {
            if(error.message) {
                toast.error(error.message || 'Error saving Xero settings', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
            return;
        } finally {
            setIsFetching(false);
        }
    };

    const handleTenatFetch = async () => {
        try {
            setIsFetching(true);
            const response = await xeroService.getTenants();
            setErrors({});
            // Ensure tenants is always an array
            const tenantsData = Array.isArray(response?.data) ? response.data : [];
            setTenants(tenantsData);

            if(tenantsData.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    xero_tenant: tenantsData[0]
                }));
            }

        } catch (error) {
            // Reset tenants to empty array on error to prevent map errors
            setTenants([]);
            if(error.message) {
                toast.error(error.message || 'Error saving Xero settings', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
            return;
        } finally {
            setIsFetching(false);
        }
    };

    const handleAccountFetch = async () => {
        try {
            setIsFetching(true);
            const response = await xeroService.getAccounts();
            setErrors({});
            // Ensure accounts is always an array
            setAccounts(Array.isArray(response?.data) ? response.data : []);
        } catch (error) {
            // Reset accounts to empty array on error to prevent map errors
            setAccounts([]);
            if(error.message) {
                toast.error(error.message || 'Error saving Xero settings', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
            return;
        } finally {
            setIsFetching(false);
        }
    };

    const handleContactGroupFetch = async () => {

        try {
            setIsFetching(true);
            const response = await xeroService.getContactGroups();
            setErrors({});
            // Ensure contactGroups is always an array
            setContactGroups(Array.isArray(response?.data) ? response.data : []);
        }  catch (error) {
            // Reset contactGroups to empty array on error to prevent map errors
            setContactGroups([]);
            if(error.message) {
                toast.error(error.message || 'Error saving Xero settings', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
            return;
        } finally {
            setIsFetching(false);
        }
    };

    const isSubmitting = isLoading || isSaving || isFetching;

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Xero Settings</h5>
                </div>
                <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-primary" onClick={handleAuthorize} disabled={isSubmitting}>Authorize App</button>
                </div>
            </div>
            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-6">
                        <div className="row">
                            <div className="col-md-12">
                                <CommonInput
                                    name="xero_client_id"
                                    label="Xero Client ID"
                                    value={formData.xero_client_id}
                                    onChange={handleChange}
                                    errors={errors}
                                    required={true}
                                />
                            </div>
                            <div className="col-md-12">
                                <CommonInput
                                    name="xero_client_secret"
                                    label="Xero Client Secret"
                                    value={formData.xero_client_secret}
                                    onChange={handleChange}
                                    errors={errors}
                                    required={true}
                                />
                            </div>
                            <div className="col-md-12">
                                <CommonInput
                                    name="xero_redirect_uri"
                                    label="Xero Redirect URI"
                                    value={formData.xero_redirect_uri}
                                    onChange={handleChange}
                                    errors={errors}
                                    required={true}
                                />
                            </div>
                            <div className="col-md-12">
                                <CommonSelect
                                    name="xero_status"
                                    label="Status"
                                    value={formData.xero_status}
                                    onChange={(value) => handleSelectChange('xero_status', value)}
                                    options={[{ label: 'Active', id: '1' }, { label: 'Disabled', id: '0' }]}
                                    errors={errors}
                                    required={true}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-12">
                                <CommonTextarea
                                    name="access_token"
                                    label="Xero Access Token"
                                    value={formData.access_token}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                    required={false}
                                    rows={2}
                                />
                            </div>
                            <div className="col-md-12">
                                <CommonTextarea
                                    name="refresh_token"
                                    label="Xero Refresh Token"
                                    value={formData.refresh_token}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                    required={false}
                                    rows={2}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">Access Token Expires</label>
                                    <span className="badge bg-secondary">{formData.expires}</span>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">Refresh Token Expires</label>
                                    <span className="badge bg-secondary">{formData.refresh_token_expires}</span>
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className="col-md-6">

                        <div className="row">
                            <div className="col-md-12">
                                <CommonSelect
                                    name="xero_tenant"
                                    label="Xero Tenant"
                                    value={formData.xero_tenant}
                                    onChange={(value) => handleSelectChange('xero_tenant', value)}
                                    options={tenants}
                                    isSubmitting={isSubmitting}
                                    errors={errors}
                                    required={false}
                                    placeholder="Select Xero organization..."
                                    actionButtons={[
                                        {
                                            icon: 'fa fa-refresh',
                                            label: '',
                                            className: 'btn-primary',
                                            onClick: (value) => handleTenatFetch()
                                        }
                                    ]}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-12">
                                <CommonSelect
                                    name="purchase_order_account"
                                    label="Purchase Order Account"
                                    value={formData.purchase_order_account}
                                    onChange={(value) => handleSelectChange('purchase_order_account', value)}
                                    options={accounts}
                                    isSubmitting={isSubmitting}
                                    errors={errors}
                                    required={false}
                                    placeholder="Select purchase order account..."
                                    actionButtons={[
                                        {
                                            icon: 'fa fa-refresh',
                                            label: '',
                                            className: 'btn-primary',
                                            onClick: (value) => handleAccountFetch()
                                        }
                                    ]}
                                />
                            </div>
                            <div className="col-md-12">
                                <CommonSelect
                                    name="job_order_account"
                                    label="Job Order Account"
                                    value={formData.job_order_account}
                                    onChange={(value) => handleSelectChange('job_order_account', value)}
                                    options={accounts}
                                    isSubmitting={isSubmitting}
                                    errors={errors}
                                    required={false}
                                    placeholder="Select job order account..."
                                />
                            </div>
                            <div className="col-md-12">
                                <CommonSelect
                                    name="job_order_lpo_account"
                                    label="Job Order LPO Account"
                                    value={formData.job_order_lpo_account}
                                    onChange={(value) => handleSelectChange('job_order_lpo_account', value)}
                                    options={accounts}
                                    isSubmitting={isSubmitting}
                                    errors={errors}
                                    required={false}
                                    placeholder="Select job order LPO account..."
                                />
                            </div>
                            <div className="col-md-12">
                                <CommonSelect
                                    name="supplier_contact_group"
                                    label="Supplier Contact Group"
                                    value={formData.supplier_contact_group}
                                    onChange={(value) => handleSelectChange('supplier_contact_group', value)}
                                    options={contactGroups}
                                    isSubmitting={isSubmitting}
                                    errors={errors}
                                    required={false}
                                    placeholder="Select Supplier contact group..."
                                    actionButtons={[
                                        {
                                            icon: 'fa fa-refresh',
                                            label: '',
                                            className: 'btn-primary',
                                            onClick: (value) => handleContactGroupFetch()
                                        }
                                    ]}
                                />
                            </div>
                            <div className="col-md-12">
                                <CommonSelect
                                    name="walkin_contact_group"
                                    label="Walkin Contact Group"
                                    value={formData.walkin_contact_group}
                                    onChange={(value) => handleSelectChange('walkin_contact_group', value)}
                                    options={contactGroups}
                                    isSubmitting={isSubmitting}
                                    errors={errors}
                                    required={false}
                                    placeholder="Select Walk-In customer contact group..."
                                />
                            </div>
                            <div className="col-md-12">
                                <CommonSelect
                                    name="lpo_contact_group"
                                    label="LPO Customer Contact Group"
                                    value={formData.lpo_contact_group}
                                    onChange={(value) => handleSelectChange('lpo_contact_group', value)}
                                    options={contactGroups}
                                    isSubmitting={isSubmitting}
                                    errors={errors}
                                    required={false}
                                    placeholder="Select LPO customer contact group..."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isFetching ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading...
                        </>
                    ) : isSaving ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
        </form>
    );
};

export default XeroForm; 